import "../../css/TiptapEditor.css";
import { ReactNode, useCallback, useMemo, useState, useRef } from "react";
import {
  errorAlertText,
  errorSubAlertText,
  MEDIA_UPLOAD_CODES,
  uploadMedia,
} from "../../utils/photoUtils";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import RectangleButton from "../Buttons/RectangleButton";
import { Editor, posToDOMRect } from "@tiptap/core";
import { EditorContent, useEditorState } from "@tiptap/react";
import { VerticalDivider } from "../Dividers/VerticalDivider";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import ImageSizeControls, {
  ImageControl,
} from "./RichTextEditorOptions/ImageSizeControls";
import InsertInlineLink from "./RichTextEditorOptions/InsertInlineLink";
import { useOnMount } from "../../hooks/useOnMount";
import AlertContainer from "../Containers/AlertContainer";
import { DarkTheme, useTheme } from "../../hooks/useTheme";
import { ThemeType } from "@markit/common.types";
import DropdownArrowIcon from "../DropdownsAndTabs/DropdownArrowIcon";

type CustomRichTextEditorProps = {
  editor: Editor;
  headerComp: ReactNode;
  isSimplified?: boolean; // Minimal editor with reduced options
  eventTheme?: ThemeType;
};

const CustomRichTextEditor = (props: CustomRichTextEditorProps) => {
  const { editor, headerComp, isSimplified, eventTheme } = props;
  const { theme } = useTheme(eventTheme, eventTheme === undefined);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  // For InsertInlineLink.tsx
  const [currentLinkData, setCurrentLinkData] = useState<{
    href: string;
    text: string;
    hasFormatting: boolean;
  } | null>(null);
  const [showLinkManagementPopup, setShowLinkManagementPopup] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [pressLinkButton, setPressLinkButton] = useState(false);

  // For Image attachment
  const [selectedImage, setSelectedImage] = useState<ImageControl | null>(null);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });

  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const isFloatingButtonHoveredRef = useRef(false);

  const styles = {
    iconButton: {
      cursor: !editor ? "not-allowed" : "pointer",
      width: 24,
      height: 24,
      borderRadius: 8,
      transition: "opacity 0.2s ease",
    },
    styleContainer: {
      gap: 7,
      paddingBlock: 4,
      paddingInline: 7,
      borderRadius: 8,
      transition: "opacity 0.2s ease",
    },
  };

  const {
    canUndo,
    canRedo,
    isBold,
    isItalic,
    isUnderlined,
    isBulletList,
    isOrderedList,
    isParagraph,
    isHeadingOne,
    isHeadingTwo,
    isHeadingThree,
    isHeadingFour,
    isHeadingFive,
    isHeadingSix,
  } = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        canUndo: ctx.editor.can().undo(),
        canRedo: ctx.editor.can().redo(),
        isBold: ctx.editor.isActive("bold"),
        isItalic: ctx.editor.isActive("italic"),
        isUnderlined: ctx.editor.isActive("underline"),
        isBulletList: ctx.editor.isActive("bulletList"),
        isOrderedList: ctx.editor.isActive("orderedList"),
        isParagraph: ctx.editor.isActive("paragraph"),
        isHeadingOne: ctx.editor.isActive("heading", { level: 1 }),
        isHeadingTwo: ctx.editor.isActive("heading", { level: 2 }),
        isHeadingThree: ctx.editor.isActive("heading", { level: 3 }),
        isHeadingFour: ctx.editor.isActive("heading", { level: 4 }),
        isHeadingFive: ctx.editor.isActive("heading", { level: 5 }),
        isHeadingSix: ctx.editor.isActive("heading", { level: 6 }),
      };
    },
  });

  const getCurrentSize = useCallback(
    (imageElement: HTMLImageElement): string => {
      // First try to get the size from the editor's attributes
      const { state } = editor;
      const { doc } = state;

      let imagePos = -1;
      doc.descendants((node, pos) => {
        if (node.type.name === "image" && node.attrs.src === imageElement.src) {
          const domNode = editor.view.nodeDOM(pos) as HTMLElement;
          if (domNode && domNode.contains(imageElement)) {
            imagePos = pos;
            return false; // Stop searching
          }
        }
      });

      if (imagePos !== -1) {
        const imageNode = doc.nodeAt(imagePos);
        if (imageNode && imageNode.attrs.size) {
          return imageNode.attrs.size;
        }
      }
      return "medium";
    },
    [editor]
  );

  const generateImageId = useCallback(
    (imageElement: HTMLImageElement): string => {
      // Generate a unique ID based on the image's src and position
      return `${imageElement.src}-${imageElement.offsetTop}-${imageElement.offsetLeft}`;
    },
    []
  );

  const handleImageSelection = useCallback(() => {
    if (isSimplified) {
      return;
    }

    const { state } = editor;
    const { selection } = state;

    if (selection.empty) {
      setSelectedImage(null);
      return;
    }

    // Check for directly selected images first (most common case)
    const directlySelectedImages = editor.view.dom.querySelectorAll(
      ".ProseMirror-selectednode img, img.ProseMirror-selectednode"
    );

    if (directlySelectedImages.length > 1) {
      setSelectedImage(null);
      return;
    }

    if (directlySelectedImages.length === 1) {
      const imageElement = directlySelectedImages[0] as HTMLImageElement;
      const imageId = generateImageId(imageElement);
      const currentSize = getCurrentSize(imageElement);

      const newSelectedImage: ImageControl = {
        element: imageElement,
        currentSize,
        id: imageId,
      };

      setSelectedImage(newSelectedImage);
      return;
    }

    // Only check document nodes if no direct selection found
    const { from, to } = selection;
    let selectedImageElement: HTMLImageElement | null = null;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === "image" && !selectedImageElement) {
        const imageElement = editor.view.dom.querySelector(
          `img[src="${node.attrs.src}"]`
        ) as HTMLImageElement;
        if (imageElement) {
          selectedImageElement = imageElement;
          return false; // Stop searching once we find one
        }
      }
    });

    if (selectedImageElement) {
      const imageId = generateImageId(selectedImageElement);
      const currentSize = getCurrentSize(selectedImageElement);

      const newSelectedImage: ImageControl = {
        element: selectedImageElement,
        currentSize,
        id: imageId,
      };

      setSelectedImage(newSelectedImage);
    } else {
      setSelectedImage(null);
    }
  }, [editor, generateImageId, getCurrentSize, isSimplified]);

  const handleLinkSelection = useCallback(() => {
    const { state } = editor;
    const { selection } = state;
    const { from, to } = selection;

    // Get cursor position relative to the editor container
    const cursorBounds = posToDOMRect(editor.view, from, to);
    const editorElement = editor.view.dom.closest(".tiptap") || editor.view.dom;
    const editorRect = editorElement.getBoundingClientRect();

    // Calculate position relative to editor bounds
    const relativeX = cursorBounds.left - editorRect.left;
    const relativeY = cursorBounds.top - editorRect.top;

    setCursorPosition({ x: relativeX, y: relativeY });

    // Check if selection contains multiple links
    let numLinksFound = 0;
    state.doc.nodesBetween(from, to, (node) => {
      if (node.type.name === "text" && node.marks.length > 0) {
        numLinksFound++;
      }
    });

    if (numLinksFound > 1) {
      return;
    }

    // Check if the selection is on a link
    const $pos = state.doc.resolve(from);
    const linkMark = $pos
      .marks()
      .find((mark: any) => mark.type.name === "link");
    const linkNode = editor.state.doc.nodeAt(from);

    // Get the url from the current position if it's a link
    const href = linkMark
      ? linkMark.attrs.href
      : linkNode && linkNode.marks.length > 0
        ? linkNode.marks[0].attrs.href
        : null;
    if (linkNode && href) {
      const hasFormatting = linkNode.marks.length > 0;
      const text = linkNode.text || "";
      setCurrentLinkData({ href, text, hasFormatting });
      setShowLinkManagementPopup(true);

      // Get the DOM element for positioning - align bottom-left
      const coords = editor.view.coordsAtPos(selection.from);
      const modalX = coords.left; // Align to left edge of cursor
      const modalY = coords.bottom + 10;

      setModalPosition({ x: modalX, y: modalY });
      setShowLinkManagementPopup(true);
    } else {
      setCurrentLinkData(null);
      setShowLinkManagementPopup(false);
    }
  }, [editor]);

  // Handle clicking on existing links using Tiptap's selection update listener
  useOnMount(() => {
    const handleFocus = () => {
      setIsEditorFocused(true);
    };

    const handleBlur = () => {
      // Only hide if we're not hovering over the floating button
      if (!isFloatingButtonHoveredRef.current) {
        setIsEditorFocused(false);
      }
    };

    // Debounced selection handler to improve performance
    let selectionTimeout: NodeJS.Timeout;
    const handleSelectionUpdate = () => {
      clearTimeout(selectionTimeout);
      selectionTimeout = setTimeout(() => {
        handleImageSelection();
        handleLinkSelection();
      }, 50); // 50ms debounce
    };

    editor.on("focus", handleFocus);
    editor.on("blur", handleBlur);
    editor.on("selectionUpdate", handleSelectionUpdate);

    return () => {
      clearTimeout(selectionTimeout);
      editor.off("selectionUpdate", handleSelectionUpdate);
      editor.off("blur", handleBlur);
      editor.off("focus", handleFocus);
    };
  });

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Normal text",
        key: "normal_text",
        icon: isParagraph ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().setParagraph().run(),
      },
      {
        title: "H1 Heading 1",
        key: "h1_heading",
        icon: isHeadingOne ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      },
      {
        title: "H2 Heading 2",
        key: "h2_heading",
        icon: isHeadingTwo ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      },
      {
        title: "H3 Heading 3",
        key: "h3_heading",
        icon: isHeadingThree ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      },
      {
        title: "H4 Heading 4",
        key: "h4_heading",
        icon: isHeadingFour ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
      },
      {
        title: "H5 Heading 5",
        key: "h5_heading",
        icon: isHeadingFive ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().toggleHeading({ level: 5 }).run(),
      },
      {
        title: "H6 Heading 6",
        key: "h6_heading",
        icon: isHeadingSix ? "ion:checkmark" : undefined,
        onPress: () => editor.chain().focus().toggleHeading({ level: 6 }).run(),
      },
    ],
    [
      editor,
      isHeadingFive,
      isHeadingFour,
      isHeadingOne,
      isHeadingThree,
      isHeadingTwo,
      isParagraph,
      isHeadingSix,
    ]
  );

  const uploadImage = useCallback(
    (file: File) => {
      return new Promise<void>((resolve) => {
        (async () => {
          const uploadData = await uploadMedia(file, false);
          if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
            setAlertText({
              header: errorAlertText(uploadData.uploadCode),
              subHeader: errorSubAlertText(uploadData.uploadCode),
            });
            resolve();
          } else {
            const editorRect = editor.view.dom.getBoundingClientRect();
            editor
              .chain()
              .focus()
              .setImage({
                src: uploadData.blobUrl,
                width: editorRect.width, // Start medium width (100% of editor width)
              })
              .run();
            resolve();
          }
        })().catch((error) => {
          console.error("Upload error:", error);
          setAlertText({
            header: "Upload Failed",
            subHeader:
              "There was an error uploading your image. Please try again.",
          });
          resolve();
        });
      });
    },
    [editor]
  );

  const insertImageOnPress = useCallback(() => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadImage(file);
      }
    };
    fileInput.click();
  }, [uploadImage]);

  const addDropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Image",
        key: "image",
        icon: "ion:image",
        onPress: insertImageOnPress,
      },
      {
        title: "Link",
        key: "link",
        icon: "ion:link",
        onPress: () => setPressLinkButton(true),
      },
    ],
    [insertImageOnPress]
  );

  const handleFormattingCommand = useCallback(
    (command: () => void, isToggled: boolean) => {
      // Ensure editor is focused
      editor.chain().focus();

      // If editor is empty, insert a placeholder text
      if (editor.isEmpty && !isToggled) {
        editor.commands.insertContent("<p></p>");
      }

      // Apply the formatting command
      command();
    },
    [editor]
  );

  const renderOptionButton = useCallback(
    (title: string, onPress: () => void, iconName: string) => (
      <div>
        <RectangleButton
          buttonLabel={title}
          onPress={onPress}
          altColor={Colors.WHITE}
          altTextColor={Colors.BLACK}
          iconLeft={
            <Icon icon={iconName} height={14} style={{ color: Colors.BLACK }} />
          }
          containerStyles={{
            padding: 10,
            borderRadius: 8,
            border: `0.5px solid ${Colors.GRAY2}`,
          }}
        />
      </div>
    ),
    []
  );

  const renderTextIconButton = useCallback(
    (
      children: ReactNode,
      onPress: () => void,
      isActive: boolean,
      tooltip: string,
      disabled?: boolean
    ) => (
      <div
        className="Centering"
        onClick={() => {
          if (editor) {
            onPress();
          }
        }}
        title={tooltip}
        style={{
          ...styles.iconButton,
          backgroundColor: isActive
            ? theme.TertiaryBG.backgroundColor
            : Colors.TRANSPARENT,
          pointerEvents: !editor || disabled ? "none" : "auto",
          cursor: !editor ? "not-allowed" : "pointer",
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor =
              theme.TertiaryBG.backgroundColor;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isActive
            ? theme.TertiaryBG.backgroundColor
            : Colors.TRANSPARENT;
        }}
      >
        {children}
      </div>
    ),
    [editor, styles.iconButton, theme.TertiaryBG.backgroundColor]
  );

  const renderInlineLinkButton = useMemo(
    () => (
      <InsertInlineLink
        editor={editor}
        currentLinkData={currentLinkData}
        setCurrentLinkData={setCurrentLinkData}
        showLinkManagementPopup={showLinkManagementPopup}
        setShowLinkManagementPopup={setShowLinkManagementPopup}
        modalPosition={modalPosition}
        setModalPosition={setModalPosition}
        pressLinkButton={pressLinkButton}
        setPressLinkButton={setPressLinkButton}
        eventTheme={eventTheme}
      />
    ),
    [
      currentLinkData,
      editor,
      eventTheme,
      modalPosition,
      pressLinkButton,
      showLinkManagementPopup,
    ]
  );

  if (!editor) {
    return null;
  }

  return (
    <div className="ColumnNormal" style={{ gap: isSimplified ? 14 : 24 }}>
      <div className="ColumnNormal" style={{ gap: 7 }}>
        {!isSimplified ? (
          <div className="AlignedRow" style={{ gap: 7 }}>
            {renderOptionButton("Image", insertImageOnPress, "ion:image")}
            {renderInlineLinkButton}
          </div>
        ) : null}
        <div
          className="AlignedRowSpaced"
          style={{ paddingBlock: isSimplified ? 0 : 7 }}
        >
          <div className="AlignedRow" style={{ gap: 14 }}>
            {!isSimplified ? (
              <>
                <DropdownMenu
                  dropdownItems={dropdownItems}
                  trigger={["click"]}
                  placement="bottomLeft"
                  altWidth={200}
                  onOpenChange={(isOpen: boolean) => setDropdownVisible(isOpen)}
                >
                  <div
                    className="AlignedRowSelect"
                    style={styles.styleContainer}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = Colors.GRAY6;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = Colors.WHITE;
                    }}
                  >
                    <span className="bodyMedium">Style</span>
                    <DropdownArrowIcon
                      isOpen={dropdownVisible}
                      height={14}
                      style={{ marginBottom: -2 }}
                    />
                  </div>
                </DropdownMenu>
                <VerticalDivider
                  height={20}
                  color={theme === DarkTheme ? Colors.BLACK1 : Colors.GRAY2}
                  thickness={0.5}
                />
              </>
            ) : null}
            {renderTextIconButton(
              <b style={{ color: theme.PrimaryText.color }}>B</b>,
              () =>
                handleFormattingCommand(
                  () => editor.chain().toggleBold().run(),
                  isBold
                ),
              isBold,
              "Bold"
            )}
            {renderTextIconButton(
              <i style={{ color: theme.PrimaryText.color }}>i</i>,
              () =>
                handleFormattingCommand(
                  () => editor.chain().toggleItalic().run(),
                  isItalic
                ),
              isItalic,
              "Italic"
            )}
            {renderTextIconButton(
              <u style={{ color: theme.PrimaryText.color }}>U</u>,
              () =>
                handleFormattingCommand(
                  () => editor.chain().toggleUnderline().run(),
                  isUnderlined
                ),
              isUnderlined,
              "Underlined"
            )}
            <VerticalDivider
              height={20}
              color={theme === DarkTheme ? Colors.BLACK1 : Colors.GRAY2}
              thickness={0.5}
            />
            {renderTextIconButton(
              <Icon
                icon={"ion:list"}
                height={18}
                style={{
                  color: theme.PrimaryText.color,
                  marginBottom: -2,
                }}
              />,
              () => editor.chain().focus().toggleBulletList().run(),
              isBulletList,
              "Bulleted list"
            )}
            {renderTextIconButton(
              <Icon
                icon={"gridicons:list-ordered"}
                height={18}
                style={{
                  color: theme.PrimaryText.color,
                  marginBottom: -2,
                }}
              />,
              () => editor.chain().focus().toggleOrderedList().run(),
              isOrderedList,
              "Numbered list"
            )}
          </div>
          {isSimplified ? (
            renderInlineLinkButton
          ) : (
            <div className="AlignedRow" style={{ gap: 14 }}>
              {renderTextIconButton(
                <Icon
                  icon="mdi:undo"
                  height={18}
                  style={{
                    color: canUndo ? theme.PrimaryText.color : Colors.GRAY2,
                    marginBottom: -2,
                  }}
                />,
                () => editor.chain().focus().undo().run(),
                false,
                "Undo",
                !canUndo
              )}
              {renderTextIconButton(
                <Icon
                  icon="mdi:redo"
                  height={18}
                  style={{
                    color: canRedo ? theme.PrimaryText.color : Colors.GRAY2,
                    marginBottom: -2,
                  }}
                />,
                () => editor.chain().focus().redo().run(),
                false,
                "Redo",
                !canRedo
              )}
            </div>
          )}
        </div>
      </div>
      <div className="ColumnNormal" style={{ gap: 14 }}>
        {headerComp}
        <div style={{ position: "relative" }}>
          <EditorContent editor={editor} />
          {!isSimplified ? (
            <>
              {isEditorFocused ? (
                <div
                  className="Centering"
                  style={{
                    position: "absolute",
                    left: -44,
                    top: cursorPosition.y - 6,
                    zIndex: 99,
                    backgroundColor: Colors.BLACK,
                    borderRadius: 100,
                    width: 28,
                    height: 28,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                    isFloatingButtonHoveredRef.current = true;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                    isFloatingButtonHoveredRef.current = false;
                  }}
                >
                  <DropdownMenu
                    dropdownItems={addDropdownItems}
                    trigger={["click"]}
                    placement="bottomLeft"
                    altWidth={200}
                  >
                    <div className="Centering">
                      <Icon
                        icon="ion:add"
                        height={18}
                        style={{ color: Colors.WHITE }}
                      />
                    </div>
                  </DropdownMenu>
                </div>
              ) : null}
              <ImageSizeControls
                editor={editor}
                selectedImage={selectedImage}
                setSelectedImage={setSelectedImage}
              />
            </>
          ) : null}
        </div>
      </div>
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon icon="mdi-image-remove" height={40} />
            <span style={{ fontSize: 16, fontWeight: "500" }}>
              {alertText.header}
            </span>
          </div>
        }
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </div>
  );
};

export default CustomRichTextEditor;
